import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserData, Goal } from '@/context/UserDataContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Stepper from './Stepper';
import { motion } from 'framer-motion';
import QuestionContainer from './form/QuestionContainer';
import { fadeInVariants, fadeInConfig, RADIO_BUTTON_ANIMATION_DELAY } from './animations/transitions';
import HealthWarningDialog from './form/shared/HealthWarningDialog';

// Import form components
import AgeInput from './form/questions/basic/AgeInput';
import GenderSelection from './form/questions/basic/GenderSelection';
import GoalsSelection from './form/questions/basic/GoalsSelection';
import DietaryRequirements from './form/questions/dietaryHabits/DietaryRequirements';
import MeatConsumption from './form/questions/dietaryHabits/MeatConsumption';
import SeafoodConsumption from './form/questions/dietaryHabits/SeafoodConsumption';
import DairyConsumption from './form/questions/dietaryHabits/DairyConsumption';
import ExerciseFrequency from './form/questions/exerciseHabits/ExerciseFrequency';
import ExerciseTypeToggle from './form/questions/exerciseHabits/ExerciseTypeToggle';
import HealthConcernsInput from './form/questions/healthConcerns/HealthConcernsInput';
import StressLevelSelection from './form/questions/healthConcerns/StressLevelSelection';
import CurrentSupplementsInput from './form/questions/healthConcerns/supplements/CurrentSupplementsInput';
import AlcoholConsumption from './form/questions/lifestyle/AlcoholConsumption';
import SmokingVaping from './form/questions/lifestyle/SmokingVaping';

type FormStep = 'basic' | 'health' | 'diet' | 'exercise' | 'lifestyle';

export type QuestionKey = 
  | 'age' 
  | 'gender' 
  | 'goals' 
  | 'supplements' 
  | 'concerns' 
  | 'stress' 
  | 'dietaryRequirements'
  | 'meat' 
  | 'seafood' 
  | 'dairy' 
  | 'frequency' 
  | 'type' 
  | 'alcohol' 
  | 'smoking';

const STEP_ORDER: FormStep[] = ['basic', 'health', 'diet', 'exercise', 'lifestyle'];

const STEP_CONFIGS = {
  basic: {
    steps: ['age', 'gender', 'goals'] as QuestionKey[],
    validate: (step: QuestionKey, data: any) => {
      const validations = {
        age: () => !data.age ? 'Please enter your age' : 
                   (data.age < 18 || data.age > 99) ? 'Age must be between 18 and 99' : null,
        gender: () => !data.gender ? 'Please select your biological sex' : null,
        goals: () => data.goals.length === 0 ? 'Please select at least one health area to improve' : null
      };
      return validations[step]?.() ?? null;
    }
  },
  health: {
    steps: ['supplements', 'concerns', 'stress'] as QuestionKey[],
    validate: (step: QuestionKey, data: any) => 
      step === 'stress' && !data.stressLevel ? 'Please indicate your stress level' : null
  },
  diet: {
    steps: ['dietaryRequirements', 'meat', 'seafood', 'dairy'] as QuestionKey[],
    validate: (step: QuestionKey, data: any) => {
      if (step === 'dietaryRequirements') return null; // No validation needed for dietary requirements
      return !data[`${step}Consumption`] ? `Please indicate your ${step} consumption frequency` : null;
    }
  },
  exercise: {
    steps: ['frequency', 'type'] as QuestionKey[],
    validate: (step: QuestionKey, data: any) => {
      if (step === 'frequency') return !data.exerciseFrequency ? 'Please select your exercise frequency' : null;
      if (step === 'type' && data.exerciseFrequency && data.exerciseFrequency !== 'never' && data.exerciseType.length === 0) {
        return 'Please select at least one type of exercise';
      }
      return null;
    }
  },
  lifestyle: {
    steps: ['alcohol', 'smoking'] as QuestionKey[],
    validate: (step: QuestionKey, data: any) => {
      if (step === 'alcohol') return !data.alcoholConsumption ? 'Please indicate your alcohol consumption frequency' : null;
      if (step === 'smoking') {
        return typeof data.smoking !== 'boolean' ? 'Please indicate if you smoke or vape' : null;
      }
      return null;
    }
  }
} as const;

const UserDataForm = () => {
  const navigate = useNavigate();
  const { userData, setUserData } = useUserData();
  const [errors, setErrors] = useState<Record<QuestionKey, string>>({} as Record<QuestionKey, string>);
  const [currentStep, setCurrentStep] = useState<FormStep>(userData.currentStep as FormStep);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentSubStep, setCurrentSubStep] = useState(0);
  const [direction, setDirection] = useState(0);
  const [showHealthWarning, setShowHealthWarning] = useState(false);

  // Initialize form state from context
  React.useEffect(() => {
    if (userData.lastQuestion) {
      // Find the step and substep for the last question
      const step = Object.entries(STEP_CONFIGS).find(([_, config]) => 
        config.steps.includes(userData.lastQuestion as QuestionKey)
      );
      
      if (step) {
        const [stepKey, config] = step;
        const stepIndex = config.steps.indexOf(userData.lastQuestion as QuestionKey);
        setCurrentStep(stepKey as FormStep);
        setCurrentSubStep(stepIndex);
      }
    }
  }, []); // Only run once on mount

  React.useEffect(() => {
    setUserData(prev => ({ ...prev, currentStep }));
  }, [currentStep, setUserData]);

  // Update lastQuestion when current question changes
  React.useEffect(() => {
    const currentStepConfig = STEP_CONFIGS[currentStep];
    const currentQuestion = currentStepConfig.steps[currentSubStep];
    setUserData(prev => ({ ...prev, lastQuestion: currentQuestion }));
  }, [currentStep, currentSubStep, setUserData]);

  const validateCurrentStep = useCallback(() => {
    const config = STEP_CONFIGS[currentStep];
    const step = config.steps[currentSubStep];
    const error = config.validate(step, userData);
    
    if (error) {
      setErrors(prev => ({ ...prev, [step]: error }));
      toast.error('Please fix the errors before continuing');
      return false;
    }
    return true;
  }, [currentStep, currentSubStep, userData]);

  const navigateToNextStep = useCallback(() => {
    if (currentStep === 'diet' && currentSubStep === 0) {
      // After dietary requirements, check which questions to skip
      const dietaryReqs = userData.dietaryRequirements || [];
      const skipMeat = dietaryReqs.some(req => 
        ['Vegetarian', 'Vegan'].includes(req)
      );
      const skipDairy = dietaryReqs.some(req => 
        ['Dairy-Free', 'Vegan'].includes(req)
      );

      // Set default values for skipped questions
      if (skipMeat) {
        setUserData(prev => ({ ...prev, meatConsumption: 'never' }));
      }
      if (skipDairy) {
        setUserData(prev => ({ ...prev, dairyConsumption: 'never' }));
      }

      // Skip to appropriate next question
      if (skipMeat && skipDairy) {
        setCurrentStep('exercise');
        setCurrentSubStep(0);
        return;
      } else if (skipMeat) {
        setCurrentSubStep(2); // Skip to dairy
        return;
      } else if (skipDairy) {
        setCurrentSubStep(1); // Skip to meat
        return;
      }
      setCurrentSubStep(1);
    } else if (currentStep === 'diet' && currentSubStep === 1) {
      // After meat question, check if we should skip dairy
      const dietaryReqs = userData.dietaryRequirements || [];
      const skipDairy = dietaryReqs.some(req => 
        ['Dairy-Free', 'Vegan'].includes(req)
      );

      if (skipDairy) {
        setCurrentSubStep(2); // Skip to seafood
        return;
      }
      setCurrentSubStep(2);
    } else if (currentStep === 'diet' && currentSubStep === 2) {
      setCurrentStep('exercise');
      setCurrentSubStep(0);
    } else if (currentStep === 'exercise' && currentSubStep === 0) {
      // Skip exercise type question if frequency is 'never'
      if (userData.exerciseFrequency === 'never') {
        setCurrentStep('lifestyle');
        setCurrentSubStep(0);
        return;
      }
      setCurrentSubStep(1);
    } else if (currentStep === 'exercise' && currentSubStep === 1) {
      setCurrentStep('lifestyle');
      setCurrentSubStep(0);
    } else if (currentSubStep < STEP_CONFIGS[currentStep].steps.length - 1) {
      setCurrentSubStep(prev => prev + 1);
    } else {
      const currentStepIndex = STEP_ORDER.indexOf(currentStep);
      if (currentStepIndex < STEP_ORDER.length - 1) {
        setCurrentStep(STEP_ORDER[currentStepIndex + 1]);
        setCurrentSubStep(0);
      }
    }
  }, [currentStep, currentSubStep, userData.exerciseFrequency, userData.dietaryRequirements]);

  const navigateToPreviousStep = useCallback(() => {
    if (currentStep === 'lifestyle' && currentSubStep === 0) {
      // Skip exercise type question if frequency is 'never'
      if (userData.exerciseFrequency === 'never') {
        setCurrentStep('exercise');
        setCurrentSubStep(0);
        return;
      }
      setCurrentStep('exercise');
      setCurrentSubStep(1);
    } else if (currentStep === 'exercise' && currentSubStep === 0) {
      // When going back from exercise, check dietary requirements
      const dietaryReqs = userData.dietaryRequirements || [];
      const skipDairy = dietaryReqs.some(req => 
        ['Dairy-Free', 'Vegan'].includes(req)
      );
      const skipMeat = dietaryReqs.some(req => 
        ['Vegetarian', 'Vegan'].includes(req)
      );

      if (skipDairy && skipMeat) {
        setCurrentStep('diet');
        setCurrentSubStep(0);
      } else if (skipDairy) {
        setCurrentStep('diet');
        setCurrentSubStep(1);
      } else {
        setCurrentStep('diet');
        setCurrentSubStep(2);
      }
    } else if (currentStep === 'diet' && currentSubStep === 2) {
      // When going back from dairy, check if we should skip meat
      const dietaryReqs = userData.dietaryRequirements || [];
      const skipMeat = dietaryReqs.some(req => 
        ['Vegetarian', 'Vegan'].includes(req)
      );

      if (skipMeat) {
        setCurrentStep('diet');
        setCurrentSubStep(0);
      } else {
        setCurrentSubStep(1);
      }
    } else if (currentStep === 'diet' && currentSubStep === 1) {
      // When going back from meat, go to dietary requirements
      setCurrentSubStep(0);
    } else if (currentSubStep > 0) {
      setCurrentSubStep(prev => prev - 1);
    } else {
      const currentStepIndex = STEP_ORDER.indexOf(currentStep);
      if (currentStepIndex > 0) {
        setCurrentStep(STEP_ORDER[currentStepIndex - 1]);
        setCurrentSubStep(STEP_CONFIGS[STEP_ORDER[currentStepIndex - 1]].steps.length - 1);
      }
    }
  }, [currentStep, currentSubStep, userData.exerciseFrequency, userData.dietaryRequirements]);

  const handleTransition = useCallback((isForward: boolean, newValue?: any) => {
    // Set direction first
    setDirection(isForward ? 1 : -1);
    
    // Start transition
    setIsTransitioning(true);
    
    // Wait for exit animation to complete before navigating
    setTimeout(() => {
      if (isForward) {
        // Use the new value if provided, otherwise use current state
        const valueToUse = newValue !== undefined ? newValue : userData.exerciseFrequency;
        if (currentStep === 'exercise' && currentSubStep === 0) {
          if (valueToUse === 'never') {
            setCurrentStep('lifestyle');
            setCurrentSubStep(0);
            // Wait for enter animation to complete before allowing next transition
            setTimeout(() => setIsTransitioning(false), 50);
            return;
          }
          setCurrentSubStep(1);
        } else {
          navigateToNextStep();
        }
      } else {
        navigateToPreviousStep();
      }
      
      // Wait for enter animation to complete before allowing next transition
      setTimeout(() => setIsTransitioning(false), 50);
    }, 300);
  }, [currentStep, currentSubStep, userData.exerciseFrequency, navigateToNextStep, navigateToPreviousStep]);

  const handleNext = useCallback((e?: React.MouseEvent | React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!validateCurrentStep()) return;

    // Special handling for exercise frequency
    if (currentStep === 'exercise' && currentSubStep === 0) {
      // Use handleTransition for consistent animation and timing
      handleTransition(true);
      return;
    }

    handleTransition(true);
  }, [currentStep, currentSubStep, userData.exerciseFrequency, validateCurrentStep, handleTransition]);

  const handleBack = useCallback((e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    handleTransition(false);
  }, [handleTransition]);

  const handleInputChange = useCallback((field: QuestionKey, value: any) => {
    setUserData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));

    // Auto-progress for radio button questions, except for the last question (smoking)
    const radioButtonFields: QuestionKey[] = ['gender', 'stress', 'meat', 'seafood', 'dairy', 'frequency', 'alcohol', 'smoking'];
    if (radioButtonFields.includes(field) && field !== 'smoking') {
      // Pass the new value to handleTransition
      handleTransition(true, value);
    }
  }, [setUserData, handleTransition]);

  const questionComponents: Record<QuestionKey, React.ReactNode> = {
    age: <AgeInput age={userData.age} onChange={(e) => handleInputChange('age', parseInt(e.target.value) || null)} error={errors.age} />,
    gender: <GenderSelection gender={userData.gender} onChange={(value) => handleInputChange('gender', value)} error={errors.gender} />,
    goals: <GoalsSelection selectedGoals={userData.goals} onToggle={(goal) => {
      setUserData(prev => {
        const existingGoal = prev.goals.find(g => g.value === goal);
        if (existingGoal) {
          // Can only remove if it's the last selected goal
          if (existingGoal.priority === prev.goals.length) {
            return {
              ...prev,
              goals: prev.goals.filter(g => g.value !== goal)
            };
          }
          return prev;
        } else {
          // Can only add if less than 3 goals
          if (prev.goals.length < 3) {
            return {
              ...prev,
              goals: [...prev.goals, { value: goal, priority: prev.goals.length + 1 }]
            };
          }
          return prev;
        }
      });
      setErrors(prev => ({ ...prev, goals: '' }));
    }} error={errors.goals} />,
    dietaryRequirements: <DietaryRequirements />,
    supplements: <CurrentSupplementsInput currentSupplements={userData.currentSupplements || []} setUserData={setUserData} error={errors.supplements} onComplete={handleNext} />,
    concerns: <HealthConcernsInput healthConcerns={userData.healthConcerns || []} setUserData={setUserData} error={errors.concerns} />,
    stress: <StressLevelSelection error={errors.stress} onComplete={() => handleInputChange('stress', userData.stressLevel)} />,
    meat: <MeatConsumption error={errors.meat} onComplete={() => handleInputChange('meat', userData.meatConsumption)} />,
    seafood: <SeafoodConsumption error={errors.seafood} onComplete={() => handleInputChange('seafood', userData.seafoodConsumption)} />,
    dairy: <DairyConsumption error={errors.dairy} onComplete={() => handleInputChange('dairy', userData.dairyConsumption)} />,
    frequency: <ExerciseFrequency error={errors.frequency} onComplete={(value) => {
      setErrors(prev => ({ ...prev, frequency: '' }));
      handleInputChange('frequency', value);
    }} />,
    type: <ExerciseTypeToggle error={errors.type} />,
    alcohol: <AlcoholConsumption error={errors.alcohol} onComplete={() => handleInputChange('alcohol', userData.alcoholConsumption)} />,
    smoking: <SmokingVaping error={errors.smoking} isLastQuestion={true} onComplete={(value) => handleInputChange('smoking', value)} />
  };

  const currentStepConfig = STEP_CONFIGS[currentStep];
  const currentQuestion = currentStepConfig.steps[currentSubStep];
  const isLastStep = currentStep === 'lifestyle' && currentSubStep === 1;
  const isFirstQuestion = currentStep === 'basic' && currentSubStep === 0;

  // Add function to check if current step is valid
  const isCurrentStepValid = useCallback(() => {
    const config = STEP_CONFIGS[currentStep];
    const step = config.steps[currentSubStep];
    const error = config.validate(step, userData);
    return !error;
  }, [currentStep, currentSubStep, userData]);

  const handleSubmit = useCallback(() => {
    // Check if user has health concerns
    if (userData.healthConcerns && userData.healthConcerns.length > 0) {
      setShowHealthWarning(true);
      return;
    }
    navigate('/results');
  }, [userData.healthConcerns, navigate]);

  const handleHealthWarningConfirm = useCallback((dangerMode: boolean) => {
    setUserData(prev => ({ ...prev, dangerMode }));
    navigate('/results');
  }, [setUserData, navigate]);

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleNext(e); }} className="fixed inset-0 flex flex-col">
      <div className="fixed top-0 left-0 right-0 z-10">
        <div className="max-w-3xl mx-auto w-full bg-background pt-12 sm:pt-24 pb-3 px-4">
          <motion.div
            variants={fadeInVariants}
            initial="hidden"
            animate="visible"
            transition={fadeInConfig}
          >
            <Stepper 
              steps={['Basic Info', 'Health', 'Diet', 'Exercise', 'Lifestyle']} 
              currentStep={STEP_ORDER.indexOf(currentStep)} 
              onStepClick={(index) => {
                if (index <= STEP_ORDER.indexOf(currentStep)) {
                  setCurrentStep(STEP_ORDER[index]);
                  setCurrentSubStep(0);
                }
              }}
            />
          </motion.div>
        </div>
      </div>

      <div className="flex-1 w-full max-w-3xl mx-auto px-6 mt-[calc(10vh+100px)] sm:mt-[calc(20vh+100px)]">
        <div className="relative h-full">
          <QuestionContainer
            isVisible={!isTransitioning}
            direction={direction}
          >
            <div className="absolute inset-0 overflow-y-auto">
              {questionComponents[currentQuestion]}
            </div>
          </QuestionContainer>
        </div>
      </div>
      
      <div className="fixed bottom-0 left-0 right-0 z-10">
        <div className="max-w-3xl mx-auto w-full p-6 bg-gradient-to-t from-background to-background/80 backdrop-blur-sm">
          <div className="max-w-3xl mx-auto flex justify-center gap-4 sm:gap-56">
            {!isFirstQuestion && (
                <Button 
                  type="button"
                  variant="ghost"
                  onClick={handleBack}
                  className="h-12 px-8 text-base font-medium rounded-full transition-all duration-300 hover:bg-primary/5 hover:text-primary text-muted-foreground"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
            )}
            
            <Button
              type="button"
              variant="default"
              size="lg"
              className="h-12 px-8 rounded-md bg-primary"
              onClick={isLastStep ? handleSubmit : handleNext}
              disabled={!isCurrentStepValid()}
            >
              {isLastStep ? 'Submit' : (
                <>
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <HealthWarningDialog
        isOpen={showHealthWarning}
        onClose={() => setShowHealthWarning(false)}
        onConfirm={(dangerMode) => handleHealthWarningConfirm(dangerMode)}
        healthConcerns={userData.healthConcerns || []}
      />
    </form>
  );
};

export default UserDataForm;
